import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Clock } from 'lucide-react';
import WheelPicker from './WheelPicker';

interface TimePickerProps {
    value: string; // HH:mm format (24h)
    onChange: (time: string) => void;
    placeholder?: string;
    className?: string;
    required?: boolean;
    size?: 'default' | 'admin';
}

const TimePicker = ({
    value,
    onChange,
    placeholder = 'Select time',
    className = '',
    required = false,
    size = 'default',
}: TimePickerProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Initial parsing state
    const [hours, setHours] = useState('12');
    const [minutes, setMinutes] = useState('00');
    const [period, setPeriod] = useState('AM');

    useEffect(() => {
        if (value) {
            const [h, m] = value.split(':');
            let hourInt = parseInt(h);
            const p = hourInt >= 12 ? 'PM' : 'AM';

            if (hourInt > 12) hourInt -= 12;
            if (hourInt === 0) hourInt = 12;

            setHours(hourInt.toString().padStart(2, '0'));
            setMinutes(m);
            setPeriod(p);
        }
    }, [value]);

    // Close on click outside
    useEffect(() => {
        if (isOpen) {
            const closeListener = (e: MouseEvent) => {
                const target = e.target as HTMLElement;
                if (!target.closest('[data-timepicker-portal]') && !containerRef.current?.contains(target)) {
                    setIsOpen(false);
                }
            };
            document.addEventListener('mousedown', closeListener);
            return () => document.removeEventListener('mousedown', closeListener);
        }
    }, [isOpen]);

    const handleTimeChange = (newHours: string, newMinutes: string, newPeriod: string) => {
        setHours(newHours);
        setMinutes(newMinutes);
        setPeriod(newPeriod);

        let hourInt = parseInt(newHours);
        if (newPeriod === 'PM' && hourInt !== 12) hourInt += 12;
        if (newPeriod === 'AM' && hourInt === 12) hourInt = 0;

        // Ensure we format correctly
        const formattedHour = hourInt.toString().padStart(2, '0');
        onChange(`${formattedHour}:${newMinutes}`);
    };

    // Options Generation
    const hourOptions = Array.from({ length: 12 }, (_, i) => {
        const val = (i + 1).toString().padStart(2, '0');
        return { label: val, value: val };
    });

    const minuteOptions = Array.from({ length: 60 }, (_, i) => {
        const val = i.toString().padStart(2, '0');
        return { label: val, value: val };
    });

    const periodOptions = [
        { label: 'AM', value: 'AM' },
        { label: 'PM', value: 'PM' },
    ];

    return (
        <div ref={containerRef} className={`relative ${className}`}>
            <div
                onClick={() => setIsOpen(!isOpen)}
                className="relative cursor-pointer"
            >
                <input
                    type="text"
                    readOnly
                    value={value ? `${hours}:${minutes} ${period}` : ''}
                    placeholder={placeholder}
                    required={required}
                    className={`w-full bg-white/5 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-transparent transition-all cursor-pointer pointer-events-none font-sans ${
                        size === 'admin'
                            ? 'h-9 pl-3 pr-8 rounded-xl text-[11px] placeholder:text-[11px] placeholder-white/30'
                            : 'py-3 md:py-4 pl-4 pr-12 rounded-2xl text-sm md:text-base placeholder-white/30'
                    }`}
                    // Prevent mobile keyboard
                    inputMode="none"
                />

                <div className={`absolute top-1/2 -translate-y-1/2 text-primary-400 ${size === 'admin' ? 'right-2.5' : 'right-4'}`}>
                    <Clock size={size === 'admin' ? 14 : 18} />
                </div>
            </div>

            {isOpen && createPortal(
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                        onClick={() => setIsOpen(false)}
                    />

                    {/* Modal */}
                    <div
                        data-timepicker-portal
                        className="relative bg-cosmic-900/40 backdrop-blur-2xl border border-white/20 rounded-2xl shadow-2xl p-6 w-80"
                    >
                        <div className="text-center text-purple-200 mb-4 font-bold uppercase text-xs tracking-wider">Select Time</div>

                        {/* Now Button */}
                        <button
                            type="button"
                            onClick={() => {
                                const now = new Date();
                                let currentHour = now.getHours();
                                const currentMinute = now.getMinutes();
                                const currentPeriod = currentHour >= 12 ? 'PM' : 'AM';

                                if (currentHour > 12) currentHour -= 12;
                                if (currentHour === 0) currentHour = 12;

                                const formattedHour = currentHour.toString().padStart(2, '0');
                                const formattedMinute = currentMinute.toString().padStart(2, '0');

                                handleTimeChange(formattedHour, formattedMinute, currentPeriod);
                            }}
                            className="w-full mb-4 py-2 bg-white/5 hover:bg-white/10 border border-purple-400/30 hover:border-purple-400/50 rounded-lg text-purple-200 hover:text-white font-medium text-sm transition-all"
                        >
                            Now
                        </button>

                        <div className="flex gap-4 items-center justify-center h-[220px]">
                            {/* Hours */}
                            <div className="w-16">
                                <WheelPicker
                                    options={hourOptions}
                                    value={hours}
                                    onChange={(v) => handleTimeChange(v as string, minutes, period)}
                                />
                            </div>

                            <span className="text-white font-bold text-xl pb-2">:</span>

                            {/* Minutes */}
                            <div className="w-16">
                                <WheelPicker
                                    options={minuteOptions}
                                    value={minutes}
                                    onChange={(v) => handleTimeChange(hours, v as string, period)}
                                />
                            </div>

                            {/* AM/PM */}
                            <div className="w-16">
                                <WheelPicker
                                    options={periodOptions}
                                    value={period}
                                    onChange={(v) => handleTimeChange(hours, minutes, v as string)}
                                />
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={() => setIsOpen(false)}
                            className="w-full mt-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 rounded-xl text-white font-bold text-sm transition-colors"
                        >
                            Done
                        </button>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default TimePicker;
